import React from 'react';
import { useAuth } from '../App';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import { LogOut, Users, Building2, UserCog, Database, Archive, CheckCircle } from 'lucide-react';

const RHHomepage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    toast.success('Déconnexion réussie');
  };

  const handleBlockClick = (blockName, route) => {
    if (route === 'rh.rc_page') {
      navigate('/rh/recrutements-generaux');
    } else if (route === 'rh.hs_page') {
      navigate('/rh/horizon-solution');
    } else if (route === 'rh.vivier_page') {
      navigate('/rh/vivier-candidatures');
    } else if (route === 'rh.rc.archive') {
      navigate('/rh/offres-archivees');
    } else if (route === 'rh.gc_page') {
      navigate('/rh/gestion-comptes');
    } else {
      toast.info(`Navigation vers ${blockName} (${route}) - À implémenter`);
    }
  };

  // Company logos URLs from the provided assets
  const companyLogos = {
    'Ma Conciergerie': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/mruq8p60_PNG_MA%20CONCIERGERIE.png',
    'Mon Evenementiel': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/o94kh96a_logo-monevenemenciel_png.png',
    'SISENS': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/gwqu631h_25.04.10%20SISENS%20LOGO.png',
    'Horizon Solution': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/md0676vy_Design%20sans%20titre%20%282%29.png',
    'Prysme': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/nzxlkuw3_logo-prysme-couleur.png'
  };

  // Logos for general recruitment (4 companies excluding Horizon Solution)
  const generalRecruitmentLogos = {
    'Ma Conciergerie': companyLogos['Ma Conciergerie'],
    'Mon Evenementiel': companyLogos['Mon Evenementiel'],
    'SISENS': companyLogos['SISENS'],
    'Prysme': companyLogos['Prysme']
  };

  return (
    <div className="rh-homepage">
      {/* Header */}
      <header className="rh-header">
        <img
          src={companyLogos['Prysme']}
          alt="Logo Prysme"
          className="rh-header-logo"
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
        <h1>Recrutement Prysme</h1>
        <Button
          onClick={handleLogout}
          variant="outline"
          size="sm"
          className="btn-outline"
          style={{ borderColor: '#eb5d2d', color: '#eb5d2d' }}
        >
          <LogOut size={16} className="mr-2" />
          Déconnexion
        </Button>
      </header>

      {/* Main Content */}
      <main className="rh-main">
        {/* Welcome Section */}
        <div className="rh-welcome">
          <h2>Gestion des Recrutements</h2>
          <p>Accédez aux différents modules de gestion des candidatures et des offres d'emploi</p>
        </div>

        {/* Main Blocks */}
        <div className="rh-blocks-grid">
          {/* General Recruitment Block */}
          <Card 
            className="rh-block cursor-pointer group"
            onClick={() => handleBlockClick('Recrutements généraux', 'rh.rc_page')}
          >
            <CardContent className="rh-block-content">
              <h3>Recrutements généraux</h3>
              <p>
                Gérez les recrutements pour les sociétés Ma Conciergerie, Mon Evenemenciel, SISENS et Prysme. 
                Accédez aux candidatures transversales et aux postes multi-entreprises.
              </p>
              <div className="rh-block-logos">
                {Object.entries(generalRecruitmentLogos).map(([company, logoUrl]) => (
                  <img
                    key={company}
                    src={logoUrl}
                    alt={`Logo ${company}`}
                    className="rh-block-logo"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Horizon Solution Specific Block */}
          <Card 
            className="rh-block cursor-pointer group"
            onClick={() => handleBlockClick('Recrutement Horizon Solution', 'rh.hs_page')}
          >
            <CardContent className="rh-block-content">
              <h3>Recrutement Horizon Solution</h3>
              <p>
                Gérez les recrutements pour de la société Horizon Solution. 
                Accédez aux candidatures et aux postes de l'entreprise.
              </p>
              <div className="rh-block-logos">
                <img
                  src={companyLogos['Horizon Solution']}
                  alt="Logo Horizon Solution"
                  className="rh-block-logo"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Secondary Blocks */}
        <div className="rh-secondary-blocks">
          <Card 
            className="rh-secondary-block cursor-pointer"
            onClick={() => handleBlockClick('Gestion des comptes', 'rh.gc_page')}
          >
            <CardContent className="p-5">
              <div className="flex items-center justify-center mb-3">
                <UserCog className="w-6 h-6 text-primary" />
              </div>
              <h4>Gestion des comptes</h4>
              <p>Administrez les comptes utilisateurs</p>
            </CardContent>
          </Card>

          <Card 
            className="rh-secondary-block cursor-pointer"
            onClick={() => handleBlockClick('Vivier des candidatures', 'rh.vivier_page')}
          >
            <CardContent className="p-5">
              <div className="flex items-center justify-center mb-3">
                <Database className="w-6 h-6 text-primary" />
              </div>
              <h4>Vivier des candidatures</h4>
              <p>Consultez et gérez le vivier des candidatures</p>
            </CardContent>
          </Card>

          <Card 
            className="rh-secondary-block cursor-pointer"
            onClick={() => handleBlockClick('Offres archivées', 'rh.rc.archive')}
          >
            <CardContent className="p-5">
              <div className="flex items-center justify-center mb-3">
                <Archive className="w-6 h-6 text-primary" />
              </div>
              <h4>Offres archivées</h4>
              <p>Consultez les offres d'emploi archivées</p>
            </CardContent>
          </Card>
        </div>

      </main>
    </div>
  );
};

export default RHHomepage;